// src/components/Menu.js
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const ChevronIcon = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform .3s ease", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

function readUserPerms() {
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    // Handles both { user: {...} } (Login.js shape) and flat { nombre, clave } (authService shape)
    return raw?.user ?? raw ?? {};
  } catch {
    return {};
  }
}

// Returns true when the permission is not explicitly false (undefined = allow, for backward compat)
const can = (p) => p === undefined || p === null ? true : Boolean(p);

export default function Menu({ onLogout }) {
  const [open, setOpen] = useState(false);
  const [openReports, setOpenReports] = useState(false);
  const [openFacturacion, setOpenFacturacion] = useState(false);
  const [openProveedores, setOpenProveedores] = useState(false);
  const [openPagos, setOpenPagos] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const perms = readUserPerms();

  let userLabel = "";
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    const u = raw?.user ?? raw ?? {};
    userLabel = u?.nombre ?? u?.Nombre ?? u?.clave ?? u?.Clave ?? "";
  } catch {}

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (location.pathname.startsWith("/reportes")) setOpenReports(true);
    if (location.pathname.startsWith("/facturacion")) setOpenFacturacion(true);
    if (location.pathname.startsWith("/proveedores")) setOpenProveedores(true);
    if (location.pathname.startsWith("/pagos")) setOpenPagos(true);
  }, [location.pathname]);

  const handleLogoutClick = () => {
    if (typeof onLogout === "function") {
      onLogout();
      navigate("/login", { replace: true });
    } else {
      localStorage.clear();
      navigate("/login", { replace: true });
    }
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <>
      {/* Top Bar */}
      <div className="lmd-topbar">
        <button className="lmd-burger-btn" aria-label="Abrir menu" onClick={() => setOpen(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>

        <span className="lmd-topbar-brand">
          <span className="lmd-topbar-logo-dot">L</span>
          LMD Integradora
        </span>

        <div className="lmd-topbar-right">
          {userLabel && (
            <div className="lmd-topbar-user">
              <div className="lmd-topbar-avatar">{userLabel.slice(0, 1).toUpperCase()}</div>
              <span className="lmd-topbar-username">{userLabel}</span>
            </div>
          )}
        </div>
      </div>

      {/* Overlay */}
      <div className={`lmd-sidebar-overlay${open ? " open" : ""}`} onClick={() => setOpen(false)} />

      {/* Sidebar */}
      <aside role="navigation" aria-label="Menu lateral" className={`lmd-sidebar${open ? " open" : ""}`}>

        <div className="lmd-sidebar-header">
          <div className="lmd-sidebar-logo">
            <div className="lmd-sidebar-logo-dot">L</div>
            LMD Integradora
          </div>
          <button className="lmd-sidebar-close" aria-label="Cerrar menu" onClick={() => setOpen(false)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {userLabel && (
          <div className="lmd-sidebar-user">
            <div className="lmd-sidebar-avatar">{userLabel.slice(0, 1).toUpperCase()}</div>
            <div className="lmd-sidebar-user-info">
              <div className="lmd-sidebar-user-name">{userLabel}</div>
              <div className="lmd-sidebar-user-role">Usuario activo</div>
            </div>
          </div>
        )}

        <ul className="lmd-nav">
          <li className="lmd-nav-section-label">Principal</li>

          {can(perms.accesoDashboard) && (
          <li>
            <Link to="/dashboard" className={`lmd-nav-link${isActive("/dashboard") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></svg>
              Dashboard
            </Link>
          </li>
          )}

          {can(perms.accesoClientes) && (
          <li>
            <Link to="/clientes" className={`lmd-nav-link${isActive("/clientes") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              Clientes
            </Link>
          </li>
          )}

          {can(perms.accesoProductos) && (
          <li>
            <Link to="/productos" className={`lmd-nav-link${isActive("/productos") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              Productos
            </Link>
          </li>
          )}

          {can(perms.accesoCotizaciones) && (
          <li>
            <Link to="/cotizaciones" className={`lmd-nav-link${isActive("/cotizaciones") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>
              Cotizaciones
            </Link>
          </li>
          )}

          <li className="lmd-nav-section-label">Operaciones</li>

          {/* Facturacion */}
          {can(perms.accesoFacturas) && (
          <li>
            <button className={`lmd-nav-link lmd-nav-dropdown-header${isActive("/facturacion") ? " active" : ""}`}
              onClick={() => setOpenFacturacion((o) => !o)} aria-expanded={openFacturacion}>
              <div className="lmd-nav-dropdown-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                Facturacion
              </div>
              <ChevronIcon open={openFacturacion} />
            </button>
            <ul className={`lmd-nav-sublist${openFacturacion ? " open" : ""}`}>
              <li>
                <Link to="/facturacion/nueva" className={`lmd-nav-sublink${isActive("/facturacion/nueva") ? " active" : ""}`}>
                  Nueva Factura
                </Link>
              </li>
              <li>
                <Link to="/facturacion/factura40" className={`lmd-nav-sublink${isActive("/facturacion/factura40") ? " active" : ""}`}>
                  Factura 4.0
                </Link>
              </li>
            </ul>
          </li>
          )}

          {/* Proveedores */}
          {can(perms.accesoProveedores) && (
          <li>
            <button className={`lmd-nav-link lmd-nav-dropdown-header${isActive("/proveedores") ? " active" : ""}`}
              onClick={() => setOpenProveedores((o) => !o)} aria-expanded={openProveedores}>
              <div className="lmd-nav-dropdown-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
                Proveedores
              </div>
              <ChevronIcon open={openProveedores} />
            </button>
            <ul className={`lmd-nav-sublist${openProveedores ? " open" : ""}`}>
              <li>
                <Link to="/proveedores/catalogo" className={`lmd-nav-sublink${isActive("/proveedores/catalogo") ? " active" : ""}`}>Catalogo</Link>
              </li>
              <li>
                <Link to="/proveedores/facturas" className={`lmd-nav-sublink${isActive("/proveedores/facturas") ? " active" : ""}`}>Facturas</Link>
              </li>
              <li>
                <Link to="/proveedores/ordenes-compra" className={`lmd-nav-sublink${isActive("/proveedores/ordenes-compra") ? " active" : ""}`}>
                  Órdenes de Compra
                </Link>
              </li>
            </ul>
          </li>
          )}

          {/* Pagos */}
          {can(perms.accesoPagos) && (
          <li>
            <button className={`lmd-nav-link lmd-nav-dropdown-header${isActive("/pagos") ? " active" : ""}`}
              onClick={() => setOpenPagos((o) => !o)} aria-expanded={openPagos}>
              <div className="lmd-nav-dropdown-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                Pagos
              </div>
              <ChevronIcon open={openPagos} />
            </button>
            <ul className={`lmd-nav-sublist${openPagos ? " open" : ""}`}>
              <li>
                <Link to="/pagos/clientes" className={`lmd-nav-sublink${isActive("/pagos/clientes") ? " active" : ""}`}>
                  Pagos de Clientes
                </Link>
              </li>
              <li>
                <Link to="/pagos/lista" className={`lmd-nav-sublink${isActive("/pagos/lista") ? " active" : ""}`}>
                  Lista de Pagos
                </Link>
              </li>
            </ul>
          </li>
          )}

          <li className="lmd-nav-section-label">Administracion</li>

          {can(perms.accesoUsuarios) && (
          <li>
            <Link to="/usuarios" className={`lmd-nav-link${isActive("/usuarios") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/><circle cx="18" cy="8" r="3"/><path d="M22 20c0-2.7-2-4.7-4-5.4"/></svg>
              Usuarios
            </Link>
          </li>
          )}

          {can(perms.accesoUsuarios) && (
          <li>
            <Link to="/empresas" className={`lmd-nav-link${isActive("/empresas") ? " active" : ""}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/><path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01"/></svg>
              Empresas
            </Link>
          </li>
          )}

          <li className="lmd-nav-section-label">Analisis</li>

          {/* Reportes */}
          {can(perms.accesoReportes) && (
          <li>
            <button className={`lmd-nav-link lmd-nav-dropdown-header${isActive("/reportes") ? " active" : ""}`}
              onClick={() => setOpenReports((o) => !o)} aria-expanded={openReports}>
              <div className="lmd-nav-dropdown-left">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                Reportes
              </div>
              <ChevronIcon open={openReports} />
            </button>
            <ul className={`lmd-nav-sublist${openReports ? " open" : ""}`}>
              <li>
                <Link to="/reportes/cartera" className={`lmd-nav-sublink${isActive("/reportes/cartera") ? " active" : ""}`}>
                  Cartera de Cobranza
                </Link>
              </li>
              <li>
                <Link to="/reportes/facturas" className={`lmd-nav-sublink${isActive("/reportes/facturas") ? " active" : ""}`}>
                  Relación de Facturas
                </Link>
              </li>
              <li>
                <Link to="/reportes/pagos" className={`lmd-nav-sublink${isActive("/reportes/pagos") ? " active" : ""}`}>
                  Pagos de Clientes
                </Link>
              </li>
            </ul>
          </li>
          )}
        </ul>

        <div className="lmd-sidebar-footer">
          <button className="lmd-logout-btn" onClick={handleLogoutClick}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Cerrar sesion
          </button>
        </div>
      </aside>
    </>
  );
}
